import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios'
import axiosRetry from 'axios-retry'
import type { ApiError, ApiResponse } from '@/types/api.types'
import type { InsufficientCreditsError } from '@/features/news/types/ai.types'
import { toast } from './toast'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Axios Instance Configuration
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10)

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Retry Configuration
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

axiosRetry(api, {
  retries: 3, // Retry up to 3 times
  retryDelay: axiosRetry.exponentialDelay, // Exponential backoff: 1s, 2s, 4s
  // Only idempotent methods (GET/HEAD/OPTIONS/PUT/DELETE) — axios-retry's own predicate,
  // not a hand-rolled allowlist. The previous `|| status >= 500` clause overrode the
  // idempotency check and made POSTs retry too, which is an upstream amplifier: one
  // `POST /news/search/batch` is up to 20 Naver calls, so 1 user action became 80 during
  // exactly the outage that produced the 5xx. `POST /clips` would have created duplicates.
  //
  // Deliberately narrower than the library default (`isNetworkOrIdempotentRequestError`),
  // which ORs in network errors for *any* method: a network error is the one case where the
  // client cannot know whether the POST already reached the server, and for batch search the
  // quota is spent either way.
  retryCondition: axiosRetry.isIdempotentRequestError,
  onRetry: (retryCount, error, requestConfig) => {
    // Log retry attempts in development
    if (import.meta.env.DEV) {
      console.warn(`[API Retry] Attempt ${retryCount} for ${requestConfig.url}`, {
        error: error.message,
        status: error.response?.status,
      })
    }
  },
})

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Request Interceptor
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Get Firebase ID token for authentication
 * This is imported dynamically to avoid circular dependencies
 */
let getIdTokenFunction: ((forceRefresh?: boolean) => Promise<string | null>) | null = null

export function setAuthTokenGetter(fn: (forceRefresh?: boolean) => Promise<string | null>) {
  getIdTokenFunction = fn
}

/**
 * 끝난 세션을 실제로 끝내는 함수. 토큰 게터와 같은 이유로 등록해서 받는다 —
 * `lib/` 가 `features/auth` 를 직접 부르면 층이 뒤집힌다.
 */
let signOutFunction: (() => Promise<void>) | null = null

export function setAuthSignOut(fn: () => Promise<void>) {
  signOutFunction = fn
}

/**
 * 세션을 끝낸다. **여기서 화면을 옮기지 않는다.**
 *
 * 로그아웃하면 Firebase 가 `onAuthStateChanged` 로 알리고, 그걸 듣고 있는
 * `authStore` 가 `user` 를 비운다 (`features/auth/stores/authStore.ts:243`).
 * 껍데기는 그 자리에서 로그아웃 상태로 다시 그려진다 — 로그인은 별도 경로가
 * 아니라 모달이라(`main.tsx` 의 라우트는 `/` 와 `/c/:code` 뿐) 옮겨 갈 데도 없다.
 * 화면을 안 옮기니 쓰던 키워드나 쓰다 만 클립도 그대로 남는다.
 *
 * 실패는 삼킨다. 인터셉터에서 새 예외를 던지면 원 요청의 거부를 가로챈다.
 */
function endSession(): void {
  void signOutFunction?.().catch(() => undefined)
}

/**
 * 진행 중인 강제 갱신 하나. 동시에 뜬 401 이 각자 갱신을 부르지 않게 붙잡아 둔다.
 *
 * 요청 인터셉터는 매 요청마다 `getIdToken()` 을 부르고 Firebase SDK 가 만료 임박분을
 * 알아서 갈아 준다. 그래도 이 함수가 필요한 건 SDK 가 아직 유효하다고 보는 토큰을
 * 서버가 만료로 판정하는 구간(시계 어긋남, 서버 쪽 이른 만료) 때문이다 — 그때는
 * `forceRefresh` 로 캐시를 건너뛰어야 실제로 새 토큰이 온다.
 */
let refreshInFlight: Promise<string | null> | null = null

function refreshIdToken(): Promise<string | null> {
  if (!refreshInFlight) {
    const started = getIdTokenFunction?.(true) ?? Promise.resolve(null)
    refreshInFlight = started
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

api.interceptors.request.use(
  async (config) => {
    // Add Firebase ID token to Authorization header if available
    if (getIdTokenFunction) {
      try {
        const idToken = await getIdTokenFunction()
        if (idToken) {
          config.headers.Authorization = `Bearer ${idToken}`
        }
      } catch (error) {
        console.error('[Auth Token Error]', error)
      }
    }

    // Add timestamp to prevent caching
    if (config.params) {
      config.params._t = Date.now()
    } else {
      config.params = { _t: Date.now() }
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      })
    }

    return config
  },
  (error) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Response Interceptor
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.url}`, response.data)
    }

    return response
  },
  (error: AxiosError<ApiError | InsufficientCreditsError>) => {
    // Extract structured error information
    const responseData = error.response?.data
    const errorMessage = (responseData as ApiError)?.message || error.message || 'An error occurred'
    const errorCode = (responseData as ApiError)?.code || error.code
    const status = error.response?.status

    console.error('[API Response Error]', {
      message: errorMessage,
      code: errorCode,
      status,
      url: error.config?.url,
      details: (responseData as ApiError)?.details,
    })

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 만료 토큰: 조용히 갱신하고 원 요청을 딱 한 번 다시 보낸다
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    //
    // 만료(`AUTH_TOKEN_EXPIRED`)와 무효(`AUTH_TOKEN_INVALID`)는 서버가 갈라서 내려주는데
    // (백엔드 `app/api/deps.py:238`) 여기서 한 분기로 묶여 있어 만료도 다시 로그인시켰다.
    //
    // 재시도는 정확히 한 번이다. 갱신한 토큰마저 거부되면 `_retriedAfterRefresh` 가 이미
    // 서 있어 아래 switch 의 로그아웃 경로로 떨어진다 — 갱신·거부가 무한히 도는 고리를
    // 막는 게 이 플래그의 전부다. 무효·폐기 토큰은 이 분기를 아예 타지 않는다.
    const config = error.config as
      | (typeof error.config & { _retriedAfterRefresh?: boolean })
      | undefined

    if (errorCode === 'AUTH_TOKEN_EXPIRED' && config && !config._retriedAfterRefresh) {
      config._retriedAfterRefresh = true

      return refreshIdToken().then((token) => {
        // 갱신 실패(로그아웃 상태 등)면 되살릴 게 없다. 평소 만료 경로와 같은 안내로 끝낸다.
        if (!token) {
          toast.authError(errorMessage)
          return Promise.reject(error)
        }
        // 요청 인터셉터가 갱신된 토큰으로 Authorization 을 다시 채운다.
        return api.request(config)
      })
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Handle errors by error code (machine-readable)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    if (errorCode) {
      switch (errorCode) {
        // 끝난 세션(401). 토큰을 냈는데 거부당했다 = 이 세션으로는 더 못 한다.
        // 안내만 띄우고 로그인된 화면을 그대로 두면 사용자는 자기가 로그아웃된 걸
        // 모른 채 남고, 누르는 것마다 계속 실패한다. 그래서 실제로 로그아웃시킨다.
        //
        // `AUTH_TOKEN_EXPIRED` 가 여기까지 왔다면 갱신한 토큰마저 거부된 두 번째
        // 실패라(위 무음 갱신 분기 참고) 되살릴 방법이 남아 있지 않다.
        case 'AUTH_TOKEN_INVALID':
        case 'AUTH_TOKEN_EXPIRED':
        case 'AUTH_USER_NOT_FOUND':
          toast.authError(errorMessage)
          endSession()
          break

        // 토큰을 아예 안 낸 401 — 끝낼 세션이 없다.
        // 위와 묶어서 로그아웃을 부르면 고리가 된다: 로그아웃된 화면이 인증 API 를
        // 부를 때마다 이 코드가 오고, 그때마다 다시 로그아웃을 부른다.
        case 'AUTH_TOKEN_MISSING':
          toast.authError(errorMessage)
          break

        // Authorization errors (403)
        case 'AUTH_INSUFFICIENT_PERMISSIONS':
          toast.error('권한 없음', errorMessage)
          break

        // 요금제 한도(403). 서버 문구가 플랜 이름과 한도를 이미 담고 있어 그대로 보여준다.
        // default 로 떨어뜨리면 '요청 오류' 라는 제목이 붙는데, 잘못 보낸 요청이 아니다.
        case 'KEYWORD_LIMIT_EXCEEDED':
          toast.error('키워드 한도', errorMessage)
          break

        // Resource not found errors (404)
        case 'RESOURCE_NOT_FOUND':
        case 'PREFERENCE_NOT_FOUND':
          toast.error('찾을 수 없음', errorMessage)
          break

        // Resource conflict errors (409)
        case 'PREFERENCE_ALREADY_EXISTS':
          toast.error('이미 존재함', errorMessage)
          break

        // Validation errors (400)
        case 'INVALID_KEYWORD':
        case 'INVALID_DISPLAY_COUNT':
        case 'INVALID_START_POSITION':
        case 'INVALID_REQUEST_BODY':
        case 'VALIDATION_ERROR':
          toast.error('입력 오류', errorMessage)
          break

        // Credit errors (429)
        case 'INSUFFICIENT_CREDITS':
          // Handle as InsufficientCreditsError for detailed credit info
          if ('details' in (responseData as ApiError)) {
            const details = (responseData as ApiError).details
            toast.creditExhausted({
              error: 'insufficient_credits',
              message: errorMessage,
              remaining_credits: details?.available_credits || 0,
              daily_limit: details?.daily_limit || 10,
              reset_time: details?.reset_time || '자정(UTC)',
            } as InsufficientCreditsError)
          } else {
            toast.error('크레딧 부족', errorMessage)
          }
          break

        case 'RATE_LIMIT_EXCEEDED':
          toast.rateLimit(errorMessage)
          break

        // 바깥 의존성이 넘어진 경우 — 네이버 API(502, 503)와 AI 서비스(500, 503).
        //
        // 접두사를 붙이지 않는다. 서버 문구가 이미 완성된 한국어 문장이라
        // "네이버 API 오류: 뉴스를 불러오지 못했어요." 처럼 겹쳐 읽히고, 어느 업체의
        // 어느 API 인지는 우리 배관 사정이지 사용자가 알아야 할 게 아니다. 사용자가
        // 할 수 있는 일은 서버 문구가 이미 말해 준다.
        case 'NAVER_API_ERROR':
        case 'NAVER_API_NETWORK_ERROR':
        case 'NAVER_API_TIMEOUT':
        case 'NAVER_API_INVALID_RESPONSE':
        case 'NAVER_API_CREDENTIALS_MISSING':
        case 'AI_SERVICE_ERROR':
        case 'AI_SERVICE_UNAVAILABLE':
        case 'AI_SUMMARIZATION_FAILED':
        case 'AI_STREAMING_ERROR':
          toast.serverError(errorMessage)
          break

        // Internal server errors (500)
        case 'INTERNAL_SERVER_ERROR':
        case 'DATABASE_ERROR':
        case 'CACHE_ERROR':
          toast.serverError(errorMessage)
          break

        // Unknown error code - fall back to status code handling
        default:
          console.warn('[Unknown Error Code]', errorCode)
          handleErrorByStatus(status, errorMessage, responseData)
      }
    } else {
      // No error code - fall back to HTTP status code handling
      handleErrorByStatus(status, errorMessage, responseData)
    }

    return Promise.reject(error)
  }
)

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helper function for status-based error handling (fallback)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function handleErrorByStatus(
  status: number | undefined,
  errorMessage: string,
  responseData: unknown
): void {
  if (status === 401) {
    toast.authError(errorMessage)
  } else if (status === 429) {
    // Check if it's a credit error with old format
    const data = responseData as InsufficientCreditsError | ApiError
    if (data && 'error' in data && data.error === 'insufficient_credits') {
      toast.creditExhausted(data as InsufficientCreditsError)
    } else {
      toast.rateLimit(errorMessage)
    }
  } else if (status === 500) {
    toast.serverError(errorMessage)
  } else if (!status) {
    toast.networkError(errorMessage)
  } else if (status >= 400 && status < 500) {
    toast.error('요청 오류', errorMessage)
  } else if (status >= 500) {
    toast.serverError(errorMessage)
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helper Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Extract error message from axios error
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || 'An error occurred'
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unknown error occurred'
}

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error)
}

/**
 * Create a typed API request wrapper
 */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const response = await api.request<ApiResponse<T>>(config)
  return response.data
}
